import type { AdminConfigHistoryDto, AdminConfigListDto, JsonValue } from '@carcheck/shared';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { apiClient } from './client';

/**
 * TanStack Query hooks for the admin runtime-config surface (AUT-131 /
 * ADMIN-19), targeting the AUT-120 READ + WRITE on the EXISTING
 * `RuntimeConfigController` (`modules/config/`):
 *
 *   - `GET   /admin/config`               → `AdminConfigListDto` (all keys, NO cursor)
 *   - `GET   /admin/config/:key/history`  → `AdminConfigHistoryDto` (newest-first)
 *   - `PATCH /admin/config/:key`          → `{ key }`
 *
 * Query-key convention (ADMIN-10 guardrail / queryClient.ts):
 *   list    → `['admin', 'config', 'list']`        (no filter args — the
 *             ~44-key set is filtered CLIENT-side; the GET takes no params)
 *   history → `['admin', 'config', 'history', key]` (per-key, lazy)
 * The PATCH mutation invalidates by the `['admin', 'config']` prefix on
 * success so the list re-reads the new value + `lastChanged*` and any open
 * history re-fetches.
 *
 * This is a SEPARATE module from the unrelated `api/config.ts` (which owns
 * the axios base-URL + timeout constants) — do not confuse the two.
 *
 * Architect guardrails (NON-NEGOTIABLE):
 * 🟠 #3 The PATCH is role + permission (`manage_settings`) + audit gated
 *      server-side (AUT-138 removed per-action TOTP step-up).
 * 🟠 #1 `value` is typed `JsonValue` end-to-end (NEVER `any`). The JSON
 *      editor parses the textarea to `JsonValue` before this layer sees it.
 * 🟠 #4 The 400 `validation_failed` (per-key range guard) is NOT
 *      auto-toasted by the shared interceptor (only 403/409 are) — the
 *      caller reads `details.issues[0].message` and surfaces it inline on
 *      the row.
 *
 * Server state lives in TanStack; the auth/session permission set lives in
 * MobX. The two never duplicate a datum.
 */

const CONFIG_BASE = '/admin/config';

/** Stable query-key prefix; the PATCH mutation invalidates against this. */
export const ADMIN_CONFIG_KEY = ['admin', 'config'] as const;

/**
 * All-keys list. NO params (architect guardrail #7 — search + type/group
 * are CLIENT-side over the bounded ~44-key set; the backend list returns
 * every key whole with no `q`/type filter). `staleTime` 0 so a PATCH
 * invalidation always refetches.
 */
export const useAdminConfigList = (): UseQueryResult<AdminConfigListDto, Error> =>
  useQuery({
    queryKey: [...ADMIN_CONFIG_KEY, 'list'],
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<AdminConfigListDto>(CONFIG_BASE, { signal });
      return data;
    },
  });

/**
 * Per-key change history. LAZY (architect guardrail #6): `enabled` is
 * driven by the row's expanded state so we never prefetch all 44 keys'
 * histories — a history fetch fires only when an operator expands a row.
 * Newest-first (the backend orders `changedAt DESC`).
 */
export const useAdminConfigHistory = (
  key: string,
  enabled: boolean,
): UseQueryResult<AdminConfigHistoryDto, Error> =>
  useQuery({
    queryKey: [...ADMIN_CONFIG_KEY, 'history', key],
    enabled,
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<AdminConfigHistoryDto>(
        `${CONFIG_BASE}/${encodeURIComponent(key)}/history`,
        { signal },
      );
      return data;
    },
  });

/** Variables for a single-key config write. */
export interface PatchConfigVariables {
  key: string;
  /** Parsed value — typed `JsonValue` (the JSON editor parses before this). */
  value: JsonValue;
}

/**
 * Low-level config-write mutation against `PATCH /admin/config/:key`. Role
 * + permission (`manage_settings`) + audit gated server-side (AUT-138
 * removed per-action TOTP step-up).
 *
 * The body is `{ value }` — the key lives in the path. Returns nothing
 * useful (`{ key }`); the caller invalidates rather than cross-patching.
 */
export const patchConfigValue = async (variables: PatchConfigVariables): Promise<void> => {
  await apiClient.patch<unknown>(`${CONFIG_BASE}/${encodeURIComponent(variables.key)}`, {
    value: variables.value,
  });
};

/**
 * Cache-helper for the config write hook. ONLY exposes `invalidate` over
 * the `['admin','config']` prefix — a successful PATCH refetches the list
 * (new value + `lastChanged*`) and any open history. No `setQueryData`:
 * the PATCH response is `{ key }`, not the list/history shape.
 */
export const useAdminConfigCache = (): {
  invalidate: () => Promise<void>;
} => {
  const queryClient = useQueryClient();
  return {
    invalidate: () => queryClient.invalidateQueries({ queryKey: ADMIN_CONFIG_KEY }),
  };
};
